/** @jsxImportSource @emotion/react */
import { saveAs } from "file-saver";
import { TableRow } from "../../../components/table-components/TableRow";
import { ReportType } from "../Reports";
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import { StyledText } from "../../../assets/typography/typography";
import { colors } from "../../../components/colors";
import { SmallBadge } from "../../../components/SmallBadge";
import { TableCellText } from "../../../components/table-components/TableCellText";
import { MenuProps } from "antd";
import { DropdownItemButton } from "../../common/DropdownItemButton";
import { useState } from "react";
import { reportingHttp, XLSXMimeType } from "../service/reports-http";
import { toastr } from "react-redux-toastr";
import { getErrorMessage } from "../../../error-handler";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { DropdownButton } from "../../../components/NewButtons";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { css } from "@emotion/react";
import { CustomReportingPeriodModal } from "./CustomReportingPeriodModal";
import moment from "moment";

type Props = {
  report: ReportType;
};

const lastTableCellStyle = css`
  display: flex;
  justify-content: end;
  align-items: cener;
`;

const tableRowStyle = css`
  cursor: default;
`;

export const ReportsTableRow = ({ report }: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const [
    isCustomReportingPeriodModalOpen,
    setIsCustomReportingPeriodModalOpen,
  ] = useState(false);
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);

  const onSubmit = async (fromDateTime: string, toDateTime?: string) => {
    setIsLoadingDownload(true);
    const startOfFromDate = moment(fromDateTime)
      .hour(0)
      .minute(0)
      .second(0)
      .format();
    // If only monthly export is allowed, set end of month as end of to date
    let endOfToDate = moment(fromDateTime)
      .endOf("month")
      .hour(23)
      .minute(59)
      .second(59)
      .format();
    if (toDateTime) {
      endOfToDate = moment(toDateTime).hour(23).minute(59).second(59).format();
    }
    try {
      const response = await reportingHttp.getReportingBlob(
        {
          organizationKeyName,
          fromDateTime: startOfFromDate,
          toDateTime: endOfToDate,
        },
        report.reportApiUrlKey
      );
      if (response.status === 204) {
        toastr.success(
          "Success",
          "This report is large, but we will send an email when it is done."
        );
        return;
      }
      const type = response.headers["content-type"] || XLSXMimeType;
      let fileName = "report.xlsx";
      const contentDisposition = response.headers["content-disposition"];
      if (contentDisposition) {
        const contentDispositionArray = contentDisposition.split("filename=");
        if (contentDispositionArray?.length > 1) {
          fileName = contentDispositionArray[1];
        }
      }
      const fileUrl = window.URL.createObjectURL(
        new Blob([response.data], {
          type,
        })
      );
      saveAs(fileUrl, fileName);
      toastr.success("Success", "Report generated and downloaded successfully");
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      toastr.error("Error", message);
    } finally {
      setIsLoadingDownload(false);
    }
  };

  const items: MenuProps["items"] = report.exportOptions.map((option, i) => ({
    key: i.toString(),
    label: (
      <DropdownItemButton
        onClick={async () => {
          await onSubmit(option.startDate.format(), option.endDate.format());
        }}
        type="button"
        icon={option.icon}
      >
        {option.label}
      </DropdownItemButton>
    ),
  }));

  return (
    <TableRow css={tableRowStyle}>
      <TableCellText className="pl-5">
        <StyledText className="bodyTextRegular mb-0" color={colors.textPrimary}>
          {report.title}
        </StyledText>
      </TableCellText>
      <TableCellText>
        <StyledText className="bodyTextRegular mb-0" color={colors.textPrimary}>
          {report.description}
        </StyledText>
      </TableCellText>
      <TableCellText>
        <SmallBadge>
          <StyledText
            className="bodyTextRegular mb-0"
            color={colors.textPrimary}
          >
            {report.category}
          </StyledText>
        </SmallBadge>
      </TableCellText>
      <TableCellText css={lastTableCellStyle}>
        <DropdownButton
          menu={{
            items: [
              ...items,
              {
                key: "custom",
                label: (
                  <DropdownItemButton
                    onClick={() => {
                      setIsCustomReportingPeriodModalOpen(true);
                    }}
                    type="button"
                    icon={<FontAwesomeIcon icon={solid("calendar-week")} />}
                  >
                    Custom period
                  </DropdownItemButton>
                ),
              },
            ],
          }}
          buttonSize="small"
          leftIcon={
            isLoadingDownload ? (
              <ActionSpinner />
            ) : (
              <FontAwesomeIcon icon={solid("upload")} />
            )
          }
        >
          Export
        </DropdownButton>
      </TableCellText>
      <CustomReportingPeriodModal
        isOpen={isCustomReportingPeriodModalOpen}
        onClose={() => setIsCustomReportingPeriodModalOpen(false)}
        onSubmit={onSubmit}
        monthYearPicker={report.monthlyExportOnly}
      />
    </TableRow>
  );
};
